import Link from "next/link";
import { Fuel, Settings2, Calendar, MapPin, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Car } from "@/lib/types";
import { formatPrice, fuelLabel, bodyLabel } from "@/lib/mock-data";

const VAT = 1.21;

interface CarCardProps {
  car: Car;
  withVat?: boolean;
}

const CarPlaceholder = ({ isElectric }: { isElectric: boolean }) => (
  <div
    className={`w-full h-48 flex items-center justify-center rounded-t-xl ${
      isElectric
        ? "bg-gradient-to-br from-blue-50 to-indigo-100"
        : "bg-gradient-to-br from-gray-50 to-gray-100"
    }`}
  >
    <svg viewBox="0 0 200 120" className="w-40 h-24 opacity-30" fill="none">
      <rect x="20" y="50" width="160" height="50" rx="8" fill="currentColor" className="text-gray-400" />
      <ellipse cx="55" cy="100" rx="18" ry="18" fill="currentColor" className="text-gray-400" />
      <ellipse cx="145" cy="100" rx="18" ry="18" fill="currentColor" className="text-gray-400" />
      <ellipse cx="55" cy="100" rx="9" ry="9" fill="white" />
      <ellipse cx="145" cy="100" rx="9" ry="9" fill="white" />
      <path d="M40 50 Q55 20 110 18 Q150 18 170 50" fill="currentColor" className="text-gray-400" />
      <rect x="70" y="22" width="55" height="28" rx="3" fill="white" opacity="0.6" />
    </svg>
  </div>
);

export default function CarCard({ car, withVat = false }: CarCardProps) {
  const displayedPrice = withVat ? Math.round(car.price_monthly * VAT) : car.price_monthly;
  return (
    <Link href={`/nabidky/${car.slug}`} className="group bg-white rounded-xl border border-gray-200 hover:border-[#1a6ef5]/30 hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col cursor-pointer">
      {/* Image */}
      <div className="relative">
        <CarPlaceholder isElectric={car.is_electric} />
        <div className="absolute top-3 left-3 flex gap-2">
          {car.in_stock ? (
            <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white text-xs font-medium">
              Skladem
            </Badge>
          ) : (
            <Badge className="bg-gray-400 hover:bg-gray-400 text-white text-xs font-medium">
              Na objednávku
            </Badge>
          )}
          {car.is_electric && (
            <Badge className="bg-[#1a6ef5] hover:bg-[#1a6ef5] text-white text-xs font-medium flex items-center gap-1">
              <Zap className="h-3 w-3" />
              EV
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Brand & Model */}
        <div className="mb-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#1a6ef5]">{car.brand}</p>
          <h3 className="font-bold text-gray-900 text-lg leading-tight">{car.model}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{car.variant}</p>
        </div>

        {/* Price */}
        <div className="mt-3 mb-4">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-extrabold text-gray-900">{formatPrice(displayedPrice)}</span>
            <span className="text-sm font-semibold text-gray-900">Kč</span>
            <span className="text-sm text-gray-400 ml-1">/ měs.</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{withVat ? "vč. DPH" : "bez DPH"}</p>
        </div>

        {/* Params */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Calendar className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span>{car.duration_months} měsíců</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span>{formatPrice(car.km_per_year)} km/rok</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Fuel className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span>{fuelLabel(car.fuel)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Settings2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="capitalize">{car.transmission}</span>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Partner + CTA */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-1.5">
            <div className="h-5 w-5 rounded bg-gray-100 flex items-center justify-center">
              <span className="text-[8px] font-bold text-gray-400">{car.partner.name.slice(0, 2).toUpperCase()}</span>
            </div>
            <span className="text-xs text-gray-400">{car.partner.name}</span>
          </div>
          <span className="bg-[#1a6ef5] group-hover:bg-[#155dd4] text-white text-xs h-8 px-3 rounded-md inline-flex items-center font-medium transition-colors">
            Mám zájem →
          </span>
        </div>
      </div>
    </Link>
  );
}
